<template>
  <div class="toxprints-categories-table">
    <v-progress-linear
      v-if="pending"
      indeterminate
    />

    <v-data-table
      v-if="response && response.enrichments"
      hide-default-footer
      :headers="headers"
      :items="response.enrichments"
      id="narrow-table"
      :items-per-page="-1"
      :custom-sort="onSort"
    >
      <template #[`header.label`]="{}">
        <div style="display: inline-block; position: relative">
          <img
            :id="`a-2-${chemicalKey(chemical)}`"
            :src="`${TOXPRINTS_URL}/render?smiles=${encodeURIComponent(chemical.smiles)}`"
            :alt="`Chemical structure of ${chemical.name || chemical.casrn || chemical.sid || 'chemical'}`"
            style="width: 150px; height: 150px"
          />
          <v-icon
            v-if="isInDSSTox(chemical)"
            class="top-right"
            color="green"
            aria-label="Present in DSSTox database"
          >
            mdi-database-check
          </v-icon>
        </div>
        <div
          v-if="showNames"
          style="font-size: smaller"
        >
          <div
            v-for="prop in ['cid', 'sid', 'casrn', 'name']"
            :key="prop"
            class="ml-1 mb-1"
          >
            <a
              v-if="['cid', 'sid', 'casrn'].includes(prop)"
              :href="`https://comptox.epa.gov/dashboard/dsstoxdb/results?search=${chemical[prop]}`"
              target="epa"
            >
              {{ chemical[prop] }}
            </a>
            <span v-else>
              {{ chemical[prop] }}
            </span>
          </div>
        </div>
        <ChemPopover
          :chemical="chemical"
          :target="`a-2-${chemicalKey(chemical)}`"
          source="toxprints"
        />
      </template>

      <template
        v-for="(h, i) in categoriesHeaders"
        #[`header.${h.value}`]="{ header }"
      >
        <div
          :key="h.value"
          class="rotate--90"
          :style="{
            color: i === 0 ? 'blue' : i === 1 || i === 2 ? 'red' : 'black',
          }"
        >
          {{ header.text }}
        </div>
      </template>

      <template #item="{ item }">
        <tr>
          <td
            :id="`txp-${item.id}`"
            style="text-align: left"
          >
            <div class="d-flex flex-column align-start">
              <button
                type="button"
                @click.prevent="$emit('navigate', 1, item.id)"
                class="d-flex flex-column align-start text-decoration-none cell-action-button"
              >
                <img
                  v-if="showToxprintStructures"
                  :src="`${TOXPRINTS_URL}/render?id=${item.id}`"
                  alt=""
                  :title="item.label"
                  v-b-tooltip
                  style="width: 150px; height: 150px"
                />

                <span style="white-space: nowrap">
                  {{ item.label }}
                </span>
              </button>

              <b-popover
                v-if="!showToxprintStructures"
                :target="`txp-${item.id}`"
                triggers="hover"
              >
                <img
                  :src="`${TOXPRINTS_URL}/render?id=${item.id}`"
                  alt=""
                />
              </b-popover>
            </div>
          </td>
          <td
            v-for="(h, i) in categoriesHeaders"
            :key="i"
            :style="{
              backgroundColor: getColorCoding(
                item[h.value].length / response.categories.find((c) => c.category === h.value).count,
              ),
            }"
          >
            <button
              v-if="item[h.value].length"
              :id="`a-${item.id}-${i}`"
              type="button"
              @click="gotoGlobaAssays(h.value, item.label)"
              class="cell-action-button"
            >
              {{ item[h.value].length }}
            </button>
            <b-popover
              v-if="item[h.value].length"
              :target="`a-${item.id}-${i}`"
              triggers="hover"
            >
              <a
                v-for="(a, j) in item[h.value]"
                :key="j"
                :href="`https://comptox.epa.gov/dashboard/assay_endpoints/${a}`"
                target="epa"
                style="display: block; white-space: nowrap"
              >
                {{ a }}
              </a>
            </b-popover>
          </td>
        </tr>
      </template>

      <template #foot>
        <tfoot>
          <tr>
            <th style="text-align: right; padding-right: 1em">Sum of Unique Assays</th>
            <th
              v-for="(h, i) in categoriesHeaders"
              :key="i"
              :style="{
                backgroundColor: getColorCoding(
                  response.sums[h.value].length / response.categories.find((c) => c.category === h.value).count,
                ),
              }"
            >
              <button
                :id="`sum-${i}`"
                v-if="response.sums[h.value].length"
                type="button"
                @click="gotoGlobaAssays(h.value)"
                class="cell-action-button"
              >
                {{ response.sums[h.value].length }}
              </button>
              <b-popover
                v-if="response.sums[h.value].length"
                :target="`sum-${i}`"
                triggers="hover"
              >
                <a
                  v-for="(a, j) in response.sums[h.value]"
                  :key="j"
                  :href="`https://comptox.epa.gov/dashboard/assay_endpoints/${a}`"
                  target="epa"
                  style="display: block; white-space: nowrap"
                >
                  {{ a }}
                </a>
              </b-popover>
            </th>
          </tr>
        </tfoot>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { TOXPRINTS_URL } from '@/main'
import ChemPopover from '@/components/chemical/ChemPopover/ChemPopover.vue'

export default {
  components: { ChemPopover },

  props: {
    chemical: {
      type: Object,
      required: true,
    },
    showNames: {
      type: Boolean,
      default: false,
    },
    showToxprintStructures: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    pending: false,
    response: null,
  }),

  computed: {
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    categoriesHeaders() {
      return this.response.categories.map((c) => ({
        text: `${c.category} (${c.count})`,
        value: c.category,
      }))
    },
    headers() {
      return [{ text: '', value: 'label', sortable: false }, ...this.categoriesHeaders]
    },
  },

  watch: {
    chemical() {
      this.getData()
    },
  },

  methods: {
    chemicalKey(chemical) {
      return chemical?.id ?? chemical?.chemId ?? chemical?.sid ?? chemical?.key
    },
    onSort(items, sortBy, sortDesc) {
      if (sortBy.length) {
        items = items.sort((a, b) =>
          !sortDesc[0] ? b[sortBy[0]].length - a[sortBy[0]].length : a[sortBy[0]].length - b[sortBy[0]].length,
        )
      }
      return items
    },
    gotoGlobaAssays(category, label) {
      let url = `/toxprints/global_assays?category=${encodeURIComponent(category)}`
      if (label) url += `&label=${encodeURIComponent(label)}`
      this.$router.push(url)
    },
    getColorCoding(f) {
      if (f > 0.8) return 'red'
      if (f > 0.7) return 'orangered'
      if (f > 0.6) return 'tomato'
      if (f > 0.5) return 'coral'
      if (f > 0.4) return 'lightsalmon'
      if (f > 0.3) return 'peachpuff'
      if (f > 0.2) return 'moccasin'
      if (f > 0.1) return 'papayawhip'
      if (f > 0.0) return 'whitesmoke'
      return 'white'
    },
    isInDSSTox(item) {
      return item.cid || item.sid || item.name || item.casrn
    },
    getData() {
      if (this.chemical) {
        this.pending = true
        this.$axios
          .post(`${TOXPRINTS_URL}/toxprints_categories`, {
            chemical: this.chemical,
          })
          .then((response) => {
            this.response = response.data
            this.pending = false
          })
          .catch(() => {
            this.pending = false
          })
      }
    },
  },

  mounted() {
    this.getData()
  },
}
</script>
<style lang="scss">
.toxprints-categories-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

#narrow-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  padding: 1px !important;

  > .v-data-table__wrapper {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: white;
  }

  tfoot th {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: white;
  }

  thead {
    th {
      vertical-align: bottom;
      white-space: nowrap;
      max-width: 3em;
      height: 180px !important;
    }
  }

  tbody {
    td {
      text-align: center;
      padding: 1px !important;
      border: 1px solid transparent;

      &:hover {
        background-color: beige !important;
      }

      a,
      .cell-action-button {
        display: block;
        width: 100%;
        height: 100%;
        text-decoration: none;
        color: black;
        padding-top: 1em;
      }
    }
  }

  table {
    th {
      text-align: center;
      height: 48px;

      border: 1px solid transparent !important;

      &:hover {
        background-color: beige !important;
      }

      a,
      .cell-action-button {
        display: block;
        width: 100%;
        height: 100%;
        text-decoration: none;
        color: black;
        padding-top: 1em;
      }
    }
  }
}

.cell-action-button {
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: inherit;
}

.cell-action-button:focus-visible {
  outline: 2px solid #1873cd;
  outline-offset: 2px;
}
</style>
